import React, { useCallback } from 'react'
import { func, node, string } from 'prop-types'
import { Grid, Box } from '@creditas-ui/react'
import { Container } from './components/Container'
import { Footer } from './components/Footer'
import { Header } from './components/Header'
import { FieldPageContext } from './context'
import { IDLE } from './constants/status'
import { useFieldPage } from './useFieldPage'
import { NextButton } from './components/NextButton'
import i18n from '~i18n/i18n'
import {
  dispatchClickToHome,
  dispatchClickToClose,
} from '~services/analyticsEvents'
import { useRoutesNavigateContext, URLS } from '~components/RoutesNavigate'
import { ButtonHeader } from '~components/ButtonHeader'
import { useIsMobile } from '~utils/hooks/useIsMobile'

const propTypes = {
  children: node.isRequired,
  eventOnLogoClick: func,
  eventOnCloseClick: func,
  status: string,
}

const FieldPage = ({
  status: initialStatus = IDLE,
  eventOnLogoClick = dispatchClickToHome,
  eventOnCloseClick = dispatchClickToClose,
  children,
}) => {
  const context = useFieldPage({ initialStatus })
  const isMobile = useIsMobile()
  const { redirect } = useRoutesNavigateContext()

  const handleClickLogo = useCallback(() => {
    eventOnLogoClick()
    redirect(URLS.website())
  }, [])

  const handleClose = useCallback(() => {
    eventOnCloseClick()
    redirect(URLS.website())
  }, [])

  const {
    isFocus,
    isValid,
    isVisible,
    loading,
    isIdle,
    handleSubmit,
    handleBack,
  } = context

  return (
    <FieldPageContext.Provider value={context}>
      <Container variant="big">
        <Grid
          height="100%"
          gridTemplateRows={{
            _: 'minmax(112px, 17.5vh) 1fr',
            '5xl': 'minmax(212px, 27.6vh) 1fr',
          }}
        >
          <Box gridColumn={{ _: '1/5', '2xl': '1/9', '5xl': '1/13' }}>
            <Header data-testid="field-page-header">
              {isIdle && (
                <ButtonHeader
                  type="logo"
                  aria-label={i18n.t('commons:buttons.logo')}
                  onClick={handleClickLogo}
                />
              )}
              {(isFocus || isValid) && (
                <ButtonHeader
                  type="back"
                  aria-label={i18n.t('commons:buttons.back')}
                  onClick={handleBack}
                />
              )}
              {(isFocus || isValid) && (
                <ButtonHeader
                  type="close"
                  aria-label={i18n.t('commons:buttons.close')}
                  onClick={handleClose}
                />
              )}
            </Header>
          </Box>
          <Box gridColumn={{ _: '1/5', '2xl': '1/9', '5xl': '1/13' }}>
            {children}
          </Box>
        </Grid>
        <Footer data-testid="field-page-footer">
          <NextButton
            isHidden={isVisible}
            isMobile={isMobile}
            isValid={isValid}
            loading={loading}
            onClick={handleSubmit}
          />
        </Footer>
      </Container>
    </FieldPageContext.Provider>
  )
}

FieldPage.propTypes = propTypes

export { FieldPage }
